import React, { useContext, useEffect, useState } from 'react';

import { Icon, Button } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { AppContext } from 'lane-shared/contexts';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { imageUrl } from 'lane-shared/helpers/formatters';

import Image from 'components/general/Image';
import { Typography } from 'components/lds';

import styles from './AndroidSmartBanner.scss';
import { ANDROID_BANNER_DISMISSED } from 'helpers/constants/localstorageKey';
import isAndroidDevice from 'helpers/isAndroidDevice';

// Helper function to check if the banner has been dismissed
function getBannerDismissalStatus(): boolean {
  if (typeof window === 'undefined') return false;

  return window.localStorage.getItem(ANDROID_BANNER_DISMISSED) === 'true';
}

// Helper function to set banner dismissal status
function setBannerDismissalStatus(): void {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(ANDROID_BANNER_DISMISSED, 'true');
}

export default function AndroidSmartBanner() {
  const { t } = useTranslation();
  const [playStorePrompt, setPlayStorePrompt] = useState<any>(null);
  const [hidePrompt, setHidePrompt] = useState<boolean>(false);
  const [hideAndroidBanner, setHideAndroidBanner] = useState<boolean>(false);
  const [initializedPrompt, setInitializedPrompt] = useState<boolean>(false);
  const [showAndroidBanner, setShowAndroidBanner] = useState<boolean>(false);
  const { whitelabel } = useContext(AppContext);

  function updateWidget() {
    if (playStorePrompt) {
      playStorePrompt.prompt();
    } else {
      // Direct link to Play Store for Android devices with null check
      if (whitelabel?.mobileAndroid) {
        window.open(whitelabel.mobileAndroid, '_blank');
      }
    }

    dismissBanner();
  }

  function dismissBanner() {
    if (playStorePrompt) {
      setHidePrompt(true);
    } else {
      setHideAndroidBanner(true);
    }

    setShowAndroidBanner(false);
    setBannerDismissalStatus();
  }

  function initializeChromeInstallPrompt(e: any) {
    // if component mounted add widegt
    if (!initializedPrompt) {
      setInitializedPrompt(true);
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setPlayStorePrompt(e);
    }
  }

  useEffect(() => {
    // Check if we should show the Android banner
    const shouldShowBanner = isAndroidDevice() && 
                             !getBannerDismissalStatus() && 
                             Boolean(whitelabel?.mobileAndroid);

    setShowAndroidBanner(shouldShowBanner);

    // Still listen for Chrome's install prompt for enhanced functionality
    window.addEventListener(
      'beforeinstallprompt',
      initializeChromeInstallPrompt
    );

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        initializeChromeInstallPrompt
      );
    };
  }, [whitelabel?.mobileAndroid]);

  // Show banner if either Chrome install prompt is available OR it's an Android device
  const shouldRenderBanner = (playStorePrompt && !hidePrompt) || 
                            (showAndroidBanner && !hideAndroidBanner);

  return shouldRenderBanner ? (
    <div className={styles.banner}>
      <Icon
        className={styles.icon}
        onClick={dismissBanner}
        name="times"
        set={ICON_SET_FONTAWESOME}
      />
      <Image 
        className={styles.logo} 
        src={imageUrl(whitelabel.profile.logo)} 
        alt={`${whitelabel.profile.name} logo`}
      />
      <div>
        <Typography type="s" mode="primary" className={styles.appText}>
          {whitelabel.profile.name}
        </Typography>
        <Typography type="xs" mode="secondary">
          {t('Download the app on the Play Store')}
        </Typography>
      </div>
      <Button 
        className={styles.viewButton} 
        type="button"
        onClick={updateWidget}
      >
        {t('View')}
      </Button>
    </div>
  ) : null;
}
